import axios, { AxiosInstance } from 'axios'
import {
  CatalogImportDto,
  CategoryInterface,
  CategoryListResponse,
  CatUploadInterface,
  ClusterInterface,
  ClusterListResponse,
  ComplexOrderInterface,
  DocumentInterface,
  DocumentLinkInterface,
  DocumentPayload,
  DownloadInterface,
  ExecutorInterface,
  ListParams,
  MailingStatusResponse,
  MotivationInterface,
  OrderInterface,
  OrderReportResponse,
  PlaceInterface,
  PriceDocumentInterface,
  PriceItemInterface,
  PriceListResponse,
  PromoInterface,
  PromoListResponse,
  SaleItemInterface,
  SaleListResponse,
  StartMailingDto,
  StockDocumentInterface,
  StockItemInterface,
  StockListResponse,
  StoreInterface,
  StoreListResponse,
  TaskInterface,
  TaskListResponse,
} from '../common/types/interfaces'
import { store } from '../store/store'
import { addNotification } from '../store/slices/notificationSlice'
import { v4 } from 'uuid'
import { EntityType, NotificationTypes, TaskStatus } from '../common/types/enums'

const baseUrl = '/api/v1/data'
let salesApi: SalesApi | null = null

class SalesApi {
  private readonly axiosInstance: AxiosInstance
  private readonly clusterAxios: AxiosInstance
  private store = store
  private timezone: string

  constructor(baseURL: string) {
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

    this.axiosInstance = axios.create({ baseURL, timeout: 60000, withCredentials: true })

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        return Promise.reject(error)
      },
    )

    this.axiosInstance.interceptors.request.use(
      (config) => {
        config.headers['X-Timezone'] = this.timezone
        return config
      },
      (error) => Promise.reject(error),
    )

    this.clusterAxios = axios.create({ baseURL: '', timeout: 60000, withCredentials: true })

    this.clusterAxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        return Promise.reject(error)
      },
    )

    this.clusterAxios.interceptors.request.use(
      (config) => {
        config.headers.Authorization = store.getState().auth.token
          ? 'Bearer ' + store.getState().auth.token
          : config.headers.Authorization
        config.headers['X-Timezone'] = this.timezone
        return config
      },
      (error) => Promise.reject(error),
    )
  }

  setToken(token: string) {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        config.headers.Authorization = store.getState().auth.token
          ? 'Bearer ' + store.getState().auth.token
          : 'Bearer ' + token
        config.headers['X-Timezone'] = this.timezone
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    this.clusterAxios.interceptors.request.use(
      (config) => {
        config.headers.Authorization = store.getState().auth.token
          ? 'Bearer ' + store.getState().auth.token
          : 'Bearer ' + token
        config.headers['X-Timezone'] = this.timezone
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  createSaleItem(payload: SaleItemInterface): Promise<SaleItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/sale_item', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали товар`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать товар',
          }),
        )
        reject(error)
      }
    })
  }

  createCategory(payload: CategoryInterface): Promise<CategoryInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/category', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали категорию`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать категорию',
          }),
        )
        reject(error)
      }
    })
  }

  createStock(payload: StockItemInterface): Promise<StockItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/stock', payload, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы добавили остатки`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось добавить остатки',
          }),
        )
        reject(error)
      }
    })
  }

  createPrice(payload: PriceItemInterface): Promise<PriceItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/price', payload, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы установили цену`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось установить цену',
          }),
        )
        reject(error)
      }
    })
  }

  createStore(payload: StoreInterface): Promise<StoreInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/store', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали торговую точку`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать торговую точку',
          }),
        )
        reject(error)
      }
    })
  }

  createPromo(payload: PromoInterface): Promise<PromoInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/promo', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали промо акцию`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать промо акцию',
          }),
        )
        reject(error)
      }
    })
  }

  updateSaleItem(payload: SaleItemInterface): Promise<SaleItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/sale_item/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали товар`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать товар',
          }),
        )
        reject(error)
      }
    })
  }

  updateCategory(payload: CategoryInterface): Promise<CategoryInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/category/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали категорию`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать категорию',
          }),
        )
        reject(error)
      }
    })
  }

  updateStock(payload: StockItemInterface): Promise<StockItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/stock/${id}`, rest, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали остатки`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать остатки',
          }),
        )
        reject(error)
      }
    })
  }

  updatePrice(payload: PriceItemInterface): Promise<PriceItemInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/price/${id}`, rest, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали цену`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать цену',
          }),
        )
        reject(error)
      }
    })
  }

  updateStore(payload: StoreInterface): Promise<StoreInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/store/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали торговую точку`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать торговую точку',
          }),
        )
        reject(error)
      }
    })
  }

  updatePromo(payload: PromoInterface): Promise<PromoInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/promo/${id}`, rest, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали промо акцию`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать промо акцию',
          }),
        )
        reject(error)
      }
    })
  }

  async getCategoryList(params?: ListParams): Promise<CategoryListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<CategoryListResponse>('/category', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getStockList(params?: ListParams): Promise<StockListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<StockListResponse>('/stock', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getPriceList(params?: ListParams): Promise<PriceListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<PriceListResponse>('/price', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getSaleItemList(params?: ListParams): Promise<SaleListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<SaleListResponse>('/sale_item', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getStoreList(params?: ListParams): Promise<StoreListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<StoreListResponse>('/store', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getClusterList(params?: ListParams): Promise<ClusterListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<ClusterListResponse>('/cluster', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getPromoList(params?: ListParams): Promise<PromoListResponse | undefined> {
    try {
      const result = await this.axiosInstance.get<PromoListResponse>('/promo', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  download(payload: DownloadInterface): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await axios.get(`${this.axiosInstance.defaults.baseURL}/file`, {
          params: payload,
          responseType: 'blob',
          withCredentials: true,
        })
        resolve(result.data)
      } catch (error) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: `Не удалось получить файл ${payload}`,
          }),
        )
        reject(error)
      }
    })
  }

  setClusterUrl(el: ClusterInterface) {
    const url = `/sales-api/${el.port}/${el.token}/api/v1/data`
    this.axiosInstance.defaults.baseURL = url
  }

  async getOrderReport(
    cluster: ClusterInterface,
    params: { gte: string; lte: string; storeId?: number; statuses?: number[] },
  ): Promise<OrderReportResponse> {
    const url = `/sales-api/${cluster.port}/${cluster.token}/api/v1/data/report/orders`
    try {
      const result = await this.clusterAxios.get<OrderReportResponse>(url, {
        params: {
          gte: params.gte,
          lte: params.lte,
          ...(params.storeId !== undefined && { storeId: params.storeId }),
          ...(params.statuses?.length && { statuses: params.statuses.join(',') }),
        },
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async getOrderList(params?: ListParams): Promise<OrderInterface[]> {
    try {
      const result = await this.axiosInstance.get<OrderInterface[]>('/order/cluster', { params })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  createOrder(payload: OrderInterface): Promise<OrderInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/order/admin', payload, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали заказ`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать заказ',
          }),
        )
        reject(error)
      }
    })
  }

  updateOrder(payload: OrderInterface): Promise<OrderInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/order/${id}`, rest, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали заказ`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать заказ',
          }),
        )
        reject(error)
      }
    })
  }

  async getDocumentList(params?: ListParams): Promise<StockDocumentInterface[]> {
    try {
      const result = await this.axiosInstance.get<StockDocumentInterface[]>('/stock/document', {
        params,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  createReceipt(payload: StockDocumentInterface): Promise<StockDocumentInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/stock/receipt', payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Приход создан`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать приход',
          }),
        )
        reject(error)
      }
    })
  }

  createWriteOff(payload: StockDocumentInterface): Promise<StockDocumentInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/stock/write-off', payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Списание создано`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать списание',
          }),
        )
        reject(error)
      }
    })
  }

  async getMovementList(params?: ListParams): Promise<StockItemInterface[]> {
    try {
      const result = await this.axiosInstance.get<StockItemInterface[]>('/stock', { params })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async getPriceDocumentList(params?: ListParams): Promise<PriceDocumentInterface[]> {
    try {
      const result = await this.axiosInstance.get<PriceDocumentInterface[]>('/price/document', {
        params,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  createPriceDocument(payload: any): Promise<PriceDocumentInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/price/document', payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Документ цен создан`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать документ цен',
          }),
        )
        reject(error)
      }
    })
  }

  startMailing(payload: StartMailingDto): Promise<{ message: string; recipientsCount: number }> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/mail/start', payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Рассылка запущена`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: error?.response?.data?.message || 'Не удалось запустить рассылку',
          }),
        )
        reject(error)
      }
    })
  }

  async getMailingStatus(
    promoId: number,
    clusterId: number,
  ): Promise<MailingStatusResponse | null> {
    try {
      const result = await this.axiosInstance.get<MailingStatusResponse>(
        `/mail/status/${promoId}`,
        {
          params: { clusterId },
        },
      )
      return result.data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  createPlace(payload: PlaceInterface): Promise<PlaceInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/place', payload)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали место`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать место',
          }),
        )
        reject(error)
      }
    })
  }

  updatePlace(payload: PlaceInterface): Promise<PlaceInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/place/${id}`, rest)
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали место`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать место',
          }),
        )
        reject(error)
      }
    })
  }

  async getPlaceList(
    params?: ListParams,
  ): Promise<{ data: PlaceInterface[]; total: number } | undefined> {
    try {
      const result = await this.axiosInstance.get('/place', { params })
      if (result) return result.data
      return undefined
    } catch (error: any) {
      throw error
    }
  }

  async getMotivationList(params?: any): Promise<MotivationInterface[]> {
    try {
      const result = await this.axiosInstance.get('/motivation', { params })
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async createMotivation(data: MotivationInterface): Promise<MotivationInterface> {
    try {
      const result = await this.axiosInstance.post('/motivation', data)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Вы создали мотивацию`,
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось создать мотивацию',
        }),
      )
      throw error
    }
  }

  async getExecutorList(params?: any): Promise<{ data: ExecutorInterface[]; total: number }> {
    try {
      const result = await this.axiosInstance.get('/executor', { params })
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async createExecutor(data: any): Promise<ExecutorInterface> {
    try {
      const result = await this.axiosInstance.post('/executor', data)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Вы создали исполнителя`,
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось создать исполнителя',
        }),
      )
      throw error
    }
  }

  async updateExecutor(data: any): Promise<ExecutorInterface> {
    try {
      const { id, ...rest } = data
      const result = await this.axiosInstance.put(`/executor/${id}`, rest)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Вы отредактировали исполнителя`,
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось отредактировать исполнителя',
        }),
      )
      throw error
    }
  }

  async attachUserToExecutor(data: { executorId: number; qrCode: string }): Promise<void> {
    try {
      await this.axiosInstance.post('/executor/attach-user', data)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: `Пользователь привязан к исполнителю`,
        }),
      )
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Не удалось привязать пользователя'
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: errorMessage,
        }),
      )
      throw error
    }
  }

  async getComplexOrderList(params?: ListParams): Promise<ComplexOrderInterface[]> {
    try {
      const result = await this.axiosInstance.get<ComplexOrderInterface[]>(
        '/complex-order/cluster',
        {
          params,
        },
      )
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async createComplexOrder(payload: ComplexOrderInterface): Promise<ComplexOrderInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.axiosInstance.post('/complex-order', payload, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы создали заявку`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось создать заявку',
          }),
        )
        reject(error)
      }
    })
  }

  async updateComplexOrder(payload: ComplexOrderInterface): Promise<ComplexOrderInterface> {
    return new Promise(async (resolve, reject) => {
      try {
        const { id, ...rest } = payload
        const result = await this.axiosInstance.put(`/complex-order/${id}`, rest, {})
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.SUCCESS,
            text: `Вы отредактировали заявку`,
          }),
        )
        resolve(result.data)
      } catch (error: any) {
        this.store.dispatch(
          addNotification({
            id: v4(),
            type: NotificationTypes.ERROR,
            text: 'Не удалось отредактировать заявку',
          }),
        )
        reject(error)
      }
    })
  }

  async getMetricsSummary(domain: string): Promise<any> {
    try {
      const result = await axios.get(`${process.env.REACT_APP_METRICS_URL}/summary/${domain}`)
      return result.data
    } catch (error: any) {
      console.error(`Failed to get metrics summary for ${domain}:`, error)
      return null
    }
  }

  async getMetricsDetailed(domain: string): Promise<any> {
    try {
      const result = await axios.get(`${process.env.REACT_APP_METRICS_URL}/stats/${domain}`)
      return result.data
    } catch (error: any) {
      console.error(`Failed to get detailed metrics for ${domain}:`, error)
      return null
    }
  }

  async importCatalog(data: CatalogImportDto): Promise<{ message: string } | undefined> {
    try {
      const result = await this.axiosInstance.post<{ message: string }>('/catalog-import', data)
      return result.data
    } catch (error: any) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: error?.response?.data?.message || 'Не удалось импортировать каталог',
        }),
      )
      throw error
    }
  }

  async getCatUploadHistory(params?: {
    sort?: string
    order?: string
  }): Promise<CatUploadInterface[] | undefined> {
    try {
      const result = await this.axiosInstance.get<CatUploadInterface[]>('/catalog-import/history', {
        params,
      })
      return result.data
    } catch (error: any) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось загрузить историю импорта',
        }),
      )
      throw error
    }
  }

  async getDocumentFileList(params?: any): Promise<{ data: DocumentInterface[]; total: number }> {
    try {
      const result = await this.axiosInstance.get('/document', { params })
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async getEntityDocumentFileList(params: {
    entityType: EntityType
    entityId?: number
  }): Promise<DocumentLinkInterface[]> {
    try {
      const result = await this.axiosInstance.get('/document/entity', { params })
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async getDocumentFile(id: number): Promise<DocumentInterface> {
    try {
      const result = await this.axiosInstance.get(`/document/${id}`)
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async createDocumentFile(payload: DocumentPayload): Promise<DocumentInterface> {
    try {
      const result = await this.axiosInstance.post('/document', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: 'Документ загружен',
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось загрузить документ',
        }),
      )
      throw error
    }
  }

  async updateDocumentFile(id: number, payload: DocumentPayload): Promise<DocumentInterface> {
    try {
      const result = await this.axiosInstance.put(`/document/${id}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: 'Документ отредактирован',
        }),
      )
      return result.data
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось отредактировать документ',
        }),
      )
      throw error
    }
  }

  async downloadDocumentFile(id: number): Promise<Blob> {
    try {
      const result = await this.axiosInstance.get(`/document/${id}/file`, {
        responseType: 'blob',
      })
      return result.data
    } catch (error: unknown) {
      console.log(error)
      throw error
    }
  }

  async deleteDocumentFile(id: number): Promise<void> {
    try {
      await this.axiosInstance.delete(`/document/${id}`)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: 'Документ удалён',
        }),
      )
    } catch (error: unknown) {
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: 'Не удалось удалить документ',
        }),
      )
      throw error
    }
  }

  getDocumentFileUrl(id: number): string {
    return `${this.axiosInstance.defaults.baseURL}/document/${id}/file`
  }

  async getTaskPool(params?: ListParams): Promise<TaskListResponse> {
    try {
      const result = await this.axiosInstance.get<TaskListResponse>('/courier/task/pool', {
        params,
      })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async getMyTasks(params?: ListParams): Promise<TaskListResponse> {
    try {
      const result = await this.axiosInstance.get<TaskListResponse>('/courier/task/my', { params })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async takeTask(taskId: number): Promise<{ message: string; task: TaskInterface }> {
    try {
      const result = await this.axiosInstance.post(`/courier/task/${taskId}/take`)
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: 'Задание взято',
        }),
      )
      return result.data
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Не удалось взять задание'
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: errorMessage,
        }),
      )
      throw error
    }
  }

  async updateTaskStatus(
    taskId: number,
    status: TaskStatus,
    comment?: string,
  ): Promise<{ message: string; task: TaskInterface }> {
    try {
      const result = await this.axiosInstance.post(`/courier/task/${taskId}/status`, {
        status,
        comment,
      })

      let successText = 'Статус обновлён'
      if (status === TaskStatus.IN_PROGRESS) successText = 'Вы в пути'
      if (status === TaskStatus.ARRIVED) successText = 'Вы прибыли'
      if (status === TaskStatus.DELIVERED) successText = 'Задание доставлено'

      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.SUCCESS,
          text: successText,
        }),
      )
      return result.data
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Не удалось обновить статус'
      this.store.dispatch(
        addNotification({
          id: v4(),
          type: NotificationTypes.ERROR,
          text: errorMessage,
        }),
      )
      throw error
    }
  }

  async getClusterTasks(params?: ListParams): Promise<TaskListResponse> {
    try {
      const result = await this.axiosInstance.get<TaskListResponse>('/task/cluster', { params })
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async getTask(taskId: number): Promise<{ task: TaskInterface }> {
    try {
      const result = await this.axiosInstance.get(`/courier/task/${taskId}`)
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async getExecutorSettings(): Promise<{ isOnShift: boolean; notifyNewTasks: boolean }> {
    try {
      const result = await this.axiosInstance.get('/executor/settings')
      return result.data
    } catch (error: any) {
      throw error
    }
  }

  async updateExecutorSettings(data: {
    isOnShift?: boolean
    notifyNewTasks?: boolean
  }): Promise<{ isOnShift: boolean; notifyNewTasks: boolean }> {
    try {
      const result = await this.axiosInstance.put('/executor/settings', data)
      return result.data
    } catch (error: any) {
      throw error
    }
  }
}

export const getSalesApi = () => {
  if (!salesApi) salesApi = new SalesApi(baseUrl)
  return salesApi
}
